import { Controller } from "@hotwired/stimulus"
import { Chart, LineController, LineElement, PointElement, LinearScale, CategoryScale, Filler, Tooltip, Legend } from "chart.js"

Chart.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Filler, Tooltip, Legend)

export default class extends Controller {
  static values = { data: Array }

  connect() {
    this.renderChart()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
  }

  renderChart() {
    const data = this.dataValue
    const labels = data.map(d => this.formatDate(d.date))
    const costData = data.map(d => d.total_cost)
    const requestData = data.map(d => d.request_count)
    const todayIndex = data.length - 1

    // Colores desde los tokens del tema (application.tailwind.css) para que la
    // gráfica siga la marca; el punto de hoy usa el ámbar de "pendiente/parcial".
    const css = getComputedStyle(document.documentElement)
    const token = (name, fallback) => css.getPropertyValue(name).trim() || fallback
    const primary = token("--color-panadata-primary", "#008577")
    const today = token("--color-status-warn-dot", "#F59E0B")
    const pointBackgroundColors = costData.map((_, i) => i === todayIndex ? today : primary)
    const pointRadii = costData.map((_, i) => i === todayIndex ? 5 : 2)

    this.chart = new Chart(this.element, {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: "Créditos",
            data: costData,
            borderColor: primary,
            backgroundColor: `color-mix(in srgb, ${primary} 12%, transparent)`,
            fill: true,
            tension: 0.3,
            yAxisID: "y",
            pointBackgroundColor: pointBackgroundColors,
            pointRadius: pointRadii,
            pointHoverRadius: 6
          },
          {
            label: "Solicitudes",
            data: requestData,
            borderColor: "#6B7280",
            backgroundColor: "rgba(107, 114, 128, 0.1)",
            fill: false,
            tension: 0.3,
            yAxisID: "y1",
            pointBackgroundColor: requestData.map((_, i) => i === todayIndex ? today : "#6B7280"),
            pointRadius: pointRadii,
            pointHoverRadius: 6
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: "index",
          intersect: false
        },
        scales: {
          y: {
            type: "linear",
            display: true,
            position: "left",
            title: { display: true, text: "Créditos" },
            beginAtZero: true
          },
          y1: {
            type: "linear",
            display: true,
            position: "right",
            title: { display: true, text: "Solicitudes" },
            beginAtZero: true,
            grid: { drawOnChartArea: false }
          },
          x: {
            ticks: {
              maxRotation: 45,
              maxTicksLimit: 15
            }
          }
        },
        plugins: {
          legend: { position: "top" },
          tooltip: {
            callbacks: {
              title: (items) => {
                if (!items.length) return ""
                const idx = items[0].dataIndex
                const raw = this.dataValue[idx]
                if (!raw) return items[0].label
                const isToday = idx === todayIndex
                return `${items[0].label}${isToday ? " (en vivo)" : ""}`
              },
              label: (context) => {
                const label = context.dataset.label || ""
                const value = context.parsed.y
                if (label === "Créditos") {
                  return ` ${label}: ${value.toFixed(2)} cr`
                }
                return ` ${label}: ${value.toLocaleString()}`
              }
            }
          }
        }
      }
    })
  }

  // Format "2026-02-15" → "Feb 15"
  formatDate(isoDate) {
    const [year, month, day] = isoDate.split("-").map(Number)
    const date = new Date(year, month - 1, day)
    return date.toLocaleDateString("es-PA", { month: "short", day: "numeric" })
  }
}
